import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  Divider,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useForm, useFieldArray } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import { RHFTextField, RHFSelect, FormProvider } from "@app/components";
import { useDispatch, useSelector } from "react-redux";
import { addModel, model, updateModel } from "../slice";
import { RootState } from "@app/redux/root-reducer";
import { RequestStatus } from "@app/constants";
import { brand } from "@app/modules/brand/slice";
import { IModelData } from "../types";
import { yupResolver } from "@hookform/resolvers/yup";
import { addModelSchema } from "../validations";
import { useRequestState } from "@app/hooks";
import { selectAddModelState, selectUpdateModelState } from "../selector";

interface AddModelModalProps {
  open: boolean;
  onClose: () => void;
  selectedBrandData?: IModelData;
}

export const AddModelModal: React.FC<AddModelModalProps> = ({
  open,
  onClose,
  selectedBrandData,
}) => {
  const dispatch = useDispatch();
  const [selectedImage, setSelectedImage] = useState<File | string | null>(
    null
  );
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const convertedVariants = selectedBrandData?.variant?.length
    ? selectedBrandData.variant[0]
        .split(",")
        .map((v: any) => ({ name: v.trim() }))
    : [{ name: "" }];

  const methods = useForm({
    resolver: yupResolver(addModelSchema) as any,
    defaultValues: {
      brandId: selectedBrandData?.brand?.id || "",
      name: selectedBrandData?.name || "",
      fuelType: selectedBrandData?.fuelType || "",
      variants: convertedVariants,
    },
  });

  const { control, reset, handleSubmit } = methods;
  const { brandData, brandsLoading } = useSelector((state: RootState) => {
    const brandState = state.brand.brand;
    return {
      brandData: brandState.data,
      brandsLoading: brandState.status === RequestStatus.RUNNING,
    };
  });

  const handleSuccess = useCallback(() => {
    reset();
    setSelectedImage(null);
    dispatch(model());
    onClose();
  }, [dispatch]);

  useRequestState({
    stateSelector: selectAddModelState,
    successMessageShown: true,
    onSuccess: handleSuccess,
  });

  useRequestState({
    stateSelector: selectUpdateModelState,
    successMessageShown: true,
    onSuccess: handleSuccess,
  });

  useEffect(() => {
    dispatch(brand());
  }, [dispatch]);

  const {
    fields: variants,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "variants",
  });

  useEffect(() => {
    if (selectedBrandData) {
      reset({
        brandId: selectedBrandData.brand?.id || "",
        name: selectedBrandData.name || "",
        fuelType: selectedBrandData.fuelType || "",
        variants: convertedVariants,
      });
      if (selectedBrandData.image) {
        setSelectedImage(selectedBrandData.image);
      } else if (selectedBrandData.brand?.image) {
        setSelectedImage(selectedBrandData.brand.image);
      } else {
        setSelectedImage(null);
      }
    }
  }, [selectedBrandData, reset]);

  const onSubmit = (data: any) => {
    try {
      setIsSubmitting(true);
      const formData = new FormData();

      formData.append("brandId", data.brandId);
      formData.append("name", data.name);
      formData.append("fuelType", data.fuelType);

      const finalVariants = data.variants.map((v: any) => v.name);

      formData.append("variant", finalVariants.join(","));

      if (selectedImage) formData.append("image", selectedImage);

      if (selectedBrandData?.id) {
        formData.append("selectedModelId", selectedBrandData.id.toString());
        dispatch(updateModel(formData));
      } else {
        dispatch(addModel(formData));
      }
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Error saving model.",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const fuelTypeData = [
    { name: "Petrol", id: "Petrol" },
    { name: "Diesel", id: "Diesel" },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {selectedBrandData ? "Edit Car Model" : "Add New Car Model"}
      </DialogTitle>
      <DialogContent>
        <FormProvider methods={methods}>
          <Stack spacing={1} mb={3}>
            <Typography fontWeight={600}>Brand Name</Typography>
            <RHFSelect
              name="brandId"
              placeholder="Select Brand"
              disabled={brandsLoading}
            >
              {brandsLoading ? (
                <MenuItem value="" disabled>
                  Loading...
                </MenuItem>
              ) : (
                brandData?.map((item: any) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))
              )}
            </RHFSelect>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="flex-start"
            mb={3}
          >
            <RHFTextField name="name" placeholder="Car Model Name" fullWidth />

            <Box
              sx={{
                height: 100,
                width: 120,
                border: "1px solid #ddd",
                borderRadius: 2,
                overflow: "hidden",
                cursor: "pointer",
                position: "relative",
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              {selectedImage ? (
                <img
                  src={
                    selectedImage instanceof File
                      ? URL.createObjectURL(selectedImage)
                      : selectedImage
                  }
                  alt="model"
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : selectedBrandData?.image ? (
                <img
                  src={selectedBrandData.image}
                  alt="model"
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                >
                  <Icon
                    icon="fluent:camera-add-48-filled"
                    width={30}
                    color="#1E91B6"
                  />
                </Stack>
              )}
              <Box
                sx={{
                  height: 100,
                  width: 120,
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  overflow: "hidden",
                  cursor: "pointer",
                  position: "relative",
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                {selectedImage ? (
                  <img
                    src={
                      selectedImage instanceof File
                        ? URL.createObjectURL(selectedImage)
                        : selectedImage
                    }
                    alt="model"
                    style={{
                      height: "100%",
                      width: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                  >
                    <Icon
                      icon="fluent:camera-add-48-filled"
                      width={30}
                      color="#1E91B6"
                    />
                  </Stack>
                )}
                <IconButton
                  sx={{
                    position: "absolute",
                    bottom: 2,
                    right: 2,
                    bgcolor: "rgba(255,255,255,0.7)",
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Icon icon="mdi:camera-retake" width={20} />
                </IconButton>
              </Box>
            </Box>
          </Stack>

          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) setSelectedImage(file);
            }}
          />

          <Stack spacing={1} mb={3}>
            <Typography fontWeight={600}>Fuel Type</Typography>
            <RHFSelect name="fuelType" placeholder="Select Fuel Type">
              {fuelTypeData.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </RHFSelect>
          </Stack>

          <Stack mb={3}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography fontWeight={600}>Variants</Typography>
              <Button
                variant="outlined"
                startIcon={<Icon icon="mdi:plus" />}
                onClick={() => append({ name: "" })}
              >
                Add More
              </Button>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {variants.map((field, index) => (
              <Stack
                key={field.id}
                direction="row"
                spacing={2}
                alignItems="center"
                mb={2}
              >
                <RHFTextField
                  name={`variants.${index}.name`}
                  placeholder={`Variant ${index + 1}`}
                  fullWidth
                />
                {variants.length > 1 && (
                  <IconButton
                    onClick={() => remove(index)}
                    sx={{ border: "1px solid #ddd" }}
                  >
                    <Icon icon="mdi:minus" />
                  </IconButton>
                )}
              </Stack>
            ))}
          </Stack>

          <Stack direction="row" justifyContent="flex-end" spacing={2} p={1}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{ px: 10, py: 1.3 }}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              loading={isSubmitting}
              onClick={handleSubmit(onSubmit)}
              sx={{ px: 10, py: 1.3 }}
            >
              Save
            </LoadingButton>
          </Stack>
        </FormProvider>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </DialogContent>
    </Dialog>
  );
};
